/**
 * Runtime config, read straight from `process.env`.
 *
 * Deliberately not `@t3-oss/env` + zod: those would ship ~400 KB of validation code to every
 * visitor just to read a few strings (this module is imported by client components). Next inlines
 * each `process.env.NEXT_PUBLIC_*` reference at build time, so the references below must stay
 * literal. Empty values (`FOO=` in `.env`) count as unset and fall back to the default.
 */
const val = (value: string | undefined): string | undefined => value?.trim() || undefined;

const url = (name: string, value: string | undefined, fallback: string): string => {
  const raw = val(value) ?? fallback;
  try {
    new URL(raw);
  } catch {
    throw new Error(`Invalid environment variable ${name}: "${raw}" is not a valid URL`);
  }
  return raw;
};

export const env = {
  // --- server only (never read from client code) ---
  CORE_API_URL: url("CORE_API_URL", process.env.CORE_API_URL, "http://localhost:3000"),
  /** Shared with the backend's `SITE_REVALIDATE_SECRET` — guards `POST /api/revalidate`. */
  REVALIDATE_SECRET: val(process.env.REVALIDATE_SECRET),

  // --- public ---
  NEXT_PUBLIC_CORE_API_URL: url(
    "NEXT_PUBLIC_CORE_API_URL",
    process.env.NEXT_PUBLIC_CORE_API_URL,
    "http://localhost:3000",
  ),
  NEXT_PUBLIC_SITE_URL: url(
    "NEXT_PUBLIC_SITE_URL",
    process.env.NEXT_PUBLIC_SITE_URL,
    "http://localhost:3200",
  ),
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: val(process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID),
  // Brand + app linking. All provisional — the name and domain change, so nothing is hardcoded.
  NEXT_PUBLIC_BRAND_NAME: val(process.env.NEXT_PUBLIC_BRAND_NAME) ?? "گراما",
  /** Custom URL scheme the mobile app registers, without `://`. */
  NEXT_PUBLIC_APP_SCHEME: val(process.env.NEXT_PUBLIC_APP_SCHEME) ?? "gerama",
  /** Android package for /.well-known/assetlinks.json. */
  NEXT_PUBLIC_ANDROID_PACKAGE:
    val(process.env.NEXT_PUBLIC_ANDROID_PACKAGE) ?? "com.grama.app.android",
  /**
   * Comma-separated SHA-256 signing-cert fingerprints (upload key AND Play/Bazaar re-signing key).
   * Defaults to the app's release key (delkok-app README) so App Links verify out of the box.
   */
  NEXT_PUBLIC_ANDROID_SHA256:
    val(process.env.NEXT_PUBLIC_ANDROID_SHA256) ??
    "2E:9F:8A:3F:9E:B9:A5:5C:A9:ED:2E:43:6A:15:C5:CB:62:A5:C7:AD:9E:B2:7C:1D:FC:E3:1E:60:C6:E8:D2:45",
  /** `<TeamID>.<bundle id>` for /.well-known/apple-app-site-association; also enables the iOS Smart App Banner. */
  NEXT_PUBLIC_IOS_APP_ID: val(process.env.NEXT_PUBLIC_IOS_APP_ID),
  /** Numeric App Store id (Smart App Banner). */
  NEXT_PUBLIC_IOS_STORE_ID: val(process.env.NEXT_PUBLIC_IOS_STORE_ID),
  // Store pages for the "open in app" banner's fallback — only the ones set are used.
  NEXT_PUBLIC_STORE_PLAY_URL: val(process.env.NEXT_PUBLIC_STORE_PLAY_URL),
  NEXT_PUBLIC_STORE_BAZAAR_URL: val(process.env.NEXT_PUBLIC_STORE_BAZAAR_URL),
  NEXT_PUBLIC_STORE_MYKET_URL: val(process.env.NEXT_PUBLIC_STORE_MYKET_URL),
  NEXT_PUBLIC_STORE_APPSTORE_URL: val(process.env.NEXT_PUBLIC_STORE_APPSTORE_URL),
  // "true" turns the DevTools guard off in a production build (staging, debugging a live issue).
  NEXT_PUBLIC_DEVTOOLS_ALLOWED: val(process.env.NEXT_PUBLIC_DEVTOOLS_ALLOWED),
  // Footer "Follow us" links — only the ones that are set are shown.
  NEXT_PUBLIC_INSTAGRAM_URL: val(process.env.NEXT_PUBLIC_INSTAGRAM_URL),
  NEXT_PUBLIC_TELEGRAM_URL: val(process.env.NEXT_PUBLIC_TELEGRAM_URL),
  NEXT_PUBLIC_TWITTER_URL: val(process.env.NEXT_PUBLIC_TWITTER_URL),
  NEXT_PUBLIC_YOUTUBE_URL: val(process.env.NEXT_PUBLIC_YOUTUBE_URL),
  NEXT_PUBLIC_APARAT_URL: val(process.env.NEXT_PUBLIC_APARAT_URL),
};
